import { useCallback, useEffect, useState } from 'react';
import {
  Beaker, Plus, Check, ChevronDown, ChevronUp, AlertTriangle,
  MapPin, Satellite, Pencil, Trash2, Loader2, Gauge, CheckCircle2, HelpCircle, Navigation,
} from 'lucide-react';
import sprayService from '../../services/spray';
import { requestLocationWithDisclosure } from '../../services/locationGate';
import trackingController from '../../services/trackingController';
import { useOrgTimezone, formatTimeInTz } from '../../utils/timezone';
import { toNum, type Numeric } from '@/utils/numeric';
import { readTankRate, latestJudgedLoad, type TankRateTone } from '@/utils/tankRateCheck';
import { convertAmount, formatAmount } from '@/utils/sprayDose';
import { readTankMotion, speedTrend, jobTurnWater, type MotionTone } from '@/utils/tankMotion';
import type {
  SprayApplication, SprayTankLoad, TankLoadChemical,
  StartTankInput, ProductUnit, TankAreaSource, TankMotion,
} from '../../types/spray';

interface TankLogPanelProps {
  spray: SprayApplication;
  /** The signed-in user is the assigned operator, so may record loads. */
  canRecord: boolean;
  /** Creator/admin — may correct or delete a load after the fact. */
  canAmend: boolean;
  onChange?: () => void;
}

const AREA_SOURCE_LABEL: Record<TankAreaSource, string> = {
  pending:     'not yet resolved',
  gps:         'from GPS track',
  manual:      'entered by hand',
  apportioned: 'split by water volume',
};

/**
 * How much confidence the area figure deserves. A GPS-derived area and a typed
 * one are not equally good evidence, and an apportioned one is not evidence at
 * all — it is arithmetic on a planned number.
 */
const AREA_SOURCE_TONE: Record<TankAreaSource, string> = {
  pending:     'text-neutral-400',
  gps:         'text-green-600',
  manual:      'text-amber-600',
  apportioned: 'text-neutral-400',
};

function fmt(n: Numeric | undefined, digits = 1): string {
  const v = toNum(n);
  return v == null ? '—' : v.toFixed(digits);
}

const RATE_TONE: Record<TankRateTone, string> = {
  ok:      'text-green-700',
  watch:   'text-amber-700',
  alert:   'text-red-700',
  unknown: 'text-neutral-500',
};

function RateIcon({ tone }: { tone: TankRateTone }) {
  if (tone === 'ok') return <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />;
  if (tone === 'unknown') return <HelpCircle className="h-3.5 w-3.5 shrink-0" />;
  return <AlertTriangle className="h-3.5 w-3.5 shrink-0" />;
}

/**
 * What this load actually put out per hectare, against what the sprayer was
 * measured at.
 *
 * Renders nothing at all for a load that has not been checked — an open tank,
 * or one recorded before this existed. An empty verdict reads as a clean one.
 */
function TankRateReadout({ tank }: { tank: SprayTankLoad }) {
  const reading = readTankRate(tank);
  if (!reading) return null;

  return (
    <div className={`text-xs ${RATE_TONE[reading.tone]}`}>
      <p className="flex items-start gap-1">
        <RateIcon tone={reading.tone} />
        <span>{reading.headline}</span>
      </p>
      {reading.detail && (
        <p className="mt-0.5 pl-[1.125rem] text-neutral-500">{reading.detail}</p>
      )}
    </div>
  );
}

const MOTION_TONE: Record<MotionTone, string> = {
  ok:      'text-green-700',
  watch:   'text-amber-700',
  unknown: 'text-neutral-700',
};

/**
 * How this load was driven: speed, implied flow and pass width against the
 * calibration, and which of them explains the rate.
 *
 * A load whose track gave nothing renders the server's reason in grey rather
 * than nothing — "no GPS points while this tank was open" is itself the answer
 * to why the tank cannot be explained.
 */
function TankMotionReadout({ tank, motion }: { tank: SprayTankLoad; motion: TankMotion }) {
  const reading = readTankMotion(motion, toNum(tank.rate_variance_pct), tank.turn_water_l != null);

  if (!reading) {
    return motion.reason ? (
      <p className="text-xs text-neutral-400 flex items-start gap-1">
        <Navigation className="h-3 w-3 shrink-0 mt-0.5" />
        <span>How it was driven: {motion.reason}.</span>
      </p>
    ) : null;
  }

  return (
    <div className="rounded border border-neutral-200 bg-white px-2.5 py-2 space-y-1.5">
      <p className="text-xs font-medium text-neutral-700 flex items-center gap-1">
        <Navigation className="h-3 w-3 shrink-0" />
        How it was driven
        {motion.is_open && <span className="font-normal text-neutral-400"> · so far</span>}
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
        {reading.facts.map(f => (
          <div key={f.label} className="contents">
            <dt className="text-neutral-500">{f.label}</dt>
            <dd className="min-w-0">
              <span className={`font-semibold ${MOTION_TONE[f.tone]}`}>{f.value}</span>
              {f.compare && <span className="text-neutral-400"> · {f.compare}</span>}
            </dd>
          </div>
        ))}
      </dl>
      {reading.findings.map(text => (
        <p key={text} className="text-xs text-neutral-600">{text}</p>
      ))}
    </div>
  );
}

/**
 * Water the job put out turning at the row ends with the sprayer on.
 *
 * It is pumped but lands on the headlands, so every load reads heavy over the
 * block's area while the rows went on at the calibration. Said once for the
 * job, because otherwise the only sign is every tank flagged high.
 */
function TurnWaterSummary({ motion, tanks }: { motion: TankMotion[]; tanks: SprayTankLoad[] }) {
  const turns = jobTurnWater(motion);
  if (!turns || turns.litres < 50) return null;
  const total = tanks
    .filter(t => t.finished_at)
    .reduce((s, t) => s + (toNum(t.water_volume_l) ?? 0), 0);
  const share = total > 0 ? Math.round((turns.litres / total) * 100) : null;
  // A check from before turning water was left out divided the whole tank
  // over the block, so the tanks read heavy; a newer one did not.
  const leftOut = tanks.some(t => t.turn_water_l != null);
  return (
    <p className="text-xs text-neutral-600 flex items-start gap-1">
      <Navigation className="h-3 w-3 shrink-0 mt-0.5" />
      <span>
        About {(Math.round(turns.litres / 10) * 10).toLocaleString('en-AU')} L
        {share != null && <> of the {Math.round(total).toLocaleString('en-AU')} L ({share}%)</>}
        {' '}went on while turning at the row ends with the sprayer on. It lands on the headlands,
        not in the block,{' '}
        {leftOut
          ? "so it is left out of each tank's rate."
          : "so tanks read heavier over the block's area than the rows actually got. Re-check tanks leaves it out of each rate."}
      </span>
    </p>
  );
}

/**
 * Median speed, load by load, in the order they went out. One number per tank
 * is enough to see a job that started at 7 km/h and finished at 9 — the
 * pattern the per-tank detail makes you open every card to find.
 */
function SpeedByTank({ motion }: { motion: TankMotion[] }) {
  const trend = speedTrend(motion);
  if (trend.length < 2) return null;
  const expected = motion.find(m => m.expected_speed_kph != null)?.expected_speed_kph ?? null;

  return (
    <div className="space-y-1">
      <p className="text-xs text-neutral-500 flex items-center gap-1">
        <Navigation className="h-3 w-3 shrink-0" />
        Speed by tank{expected != null && ` · calibrated at ${expected.toFixed(1)} km/h`}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {trend.map(t => {
          const off = expected != null && Math.abs((t.kph - expected) / expected) > 0.1;
          return (
            <span
              key={t.tank}
              className={`text-xs rounded px-1.5 py-0.5 border ${
                off ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-neutral-200 text-neutral-700'
              }`}
            >
              <span className="text-neutral-400">T{t.tank}</span> {t.kph.toFixed(1)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// One tank, expanded
// ============================================================================

function TankChemicalRow({
  chem,
  editable,
  onChange,
}: {
  chem: TankLoadChemical;
  editable: boolean;
  onChange: (patch: Partial<TankLoadChemical>) => void;
}) {
  const declared = toNum(chem.declared_amount);
  const expectedRaw = toNum(chem.expected_amount);
  const expectedUnit = chem.expected_unit || chem.declared_unit;

  // The plan restated in whatever unit the operator is measuring in, so
  // switching to mL compares 1500 mL against 1500 mL rather than against 1.5.
  // Across families (L vs kg) there is no comparison, and no warning — a false
  // variance is worse than none.
  const expected = expectedRaw == null
    ? null
    : expectedUnit === chem.declared_unit
      ? expectedRaw
      : convertAmount(expectedRaw, expectedUnit, chem.declared_unit);
  const variancePct = expected != null && declared != null && expected > 0
    ? ((declared - expected) / expected) * 100
    : null;

  // Same dose, different unit: 1.5 L becomes 1500 mL, never 1.5 mL.
  const changeUnit = (unit: ProductUnit) => {
    const converted = declared == null ? null : convertAmount(declared, chem.declared_unit, unit);
    onChange(converted == null
      ? { declared_unit: unit }
      : { declared_unit: unit, declared_amount: parseFloat(converted.toPrecision(12)) });
  };

  return (
    <div className="py-2 border-t border-neutral-100 first:border-t-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-800 truncate">{chem.product_name}</p>
          {expectedRaw != null && (
            <p className="text-xs text-neutral-500">
              Put in: <span className="font-semibold text-neutral-700">{formatAmount(expectedRaw, expectedUnit)}</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {editable ? (
            <>
              <input
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={declared ?? ''}
                onChange={e => onChange({
                  declared_amount: e.target.value === '' ? null : parseFloat(e.target.value),
                })}
                className="w-24 border border-neutral-300 rounded px-2 py-1.5 text-sm text-right"
                aria-label={`Amount of ${chem.product_name} added`}
              />
              <select
                value={chem.declared_unit}
                onChange={e => changeUnit(e.target.value as ProductUnit)}
                className="border border-neutral-300 rounded px-1.5 py-1.5 text-sm"
                aria-label={`Unit for ${chem.product_name}`}
              >
                <option value="L">L</option>
                <option value="mL">mL</option>
                <option value="kg">kg</option>
                <option value="g">g</option>
              </select>
            </>
          ) : (
            <span className="text-sm font-semibold text-neutral-800">
              {fmt(chem.declared_amount)} {chem.declared_unit}
            </span>
          )}
        </div>
      </div>

      {variancePct != null && Math.abs(variancePct) > 10 && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {variancePct > 0 ? '+' : ''}{variancePct.toFixed(0)}% against the plan
        </p>
      )}

      {editable && (
        <input
          type="text"
          value={chem.batch_number ?? ''}
          onChange={e => onChange({ batch_number: e.target.value })}
          placeholder="Batch / lot number"
          className="mt-1.5 w-full border border-neutral-200 rounded px-2 py-1 text-xs"
          aria-label={`Batch number for ${chem.product_name}`}
        />
      )}
      {!editable && chem.batch_number && (
        <p className="text-xs text-neutral-500 mt-0.5">Batch {chem.batch_number}</p>
      )}
    </div>
  );
}

function TankCard({
  tank,
  spray,
  motion,
  editable,
  canAmend,
  onSaved,
}: {
  tank: SprayTankLoad;
  spray: SprayApplication;
  motion?: TankMotion;
  editable: boolean;
  canAmend: boolean;
  onSaved: () => void;
}) {
  const timezone = useOrgTimezone();
  const [open, setOpen]         = useState(!tank.finished_at);
  const [editing, setEditing]   = useState(false);
  const [draft, setDraft]       = useState<SprayTankLoad>(tank);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => { setDraft(tank); }, [tank]);

  const isOpen = !tank.finished_at;

  const patchChemical = (chemicalId: number, patch: Partial<TankLoadChemical>) => {
    setDraft(d => ({
      ...d,
      chemicals: d.chemicals.map(c => (c.chemical_id === chemicalId ? { ...c, ...patch } : c)),
    }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const res = await sprayService.updateTankLoad(spray.id, tank.id, {
      water_volume_l: toNum(draft.water_volume_l),
      is_partial: draft.is_partial,
      sprayer_off_on_turns: draft.sprayer_off_on_turns === true,
      notes: draft.notes ?? undefined,
      chemicals: draft.chemicals.map(c => ({
        chemical_id: c.chemical_id,
        declared_amount: toNum(c.declared_amount),
        declared_unit: c.declared_unit,
        batch_number: c.batch_number ?? undefined,
      })),
    });
    setSaving(false);
    if (res.success) {
      setEditing(false);
      onSaved();
    } else {
      setError((res as { error?: { message?: string } }).error?.message || 'Could not save this tank');
    }
  };

  const remove = async () => {
    setSaving(true);
    const res = await sprayService.deleteTankLoad(spray.id, tank.id);
    setSaving(false);
    if (res.success) onSaved();
    else setError('Could not remove this tank');
  };

  const fieldsEditable = editing || (editable && isOpen);

  return (
    <div className={`rounded-lg border ${isOpen ? 'border-green-300 bg-green-50/40' : 'border-neutral-200 bg-white'}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold shrink-0
            ${isOpen ? 'bg-green-600 text-white' : 'bg-neutral-200 text-neutral-700'}`}>
            {tank.tank_number}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-neutral-800">
              Tank {tank.tank_number}
              {tank.is_partial && <span className="text-xs font-normal text-neutral-500"> · part fill</span>}
              {isOpen && <span className="text-xs font-normal text-green-700"> · spraying now</span>}
            </p>
            <p className="text-xs text-neutral-500">
              {fmt(tank.water_volume_l, 0)} L water
              {' · '}
              {formatTimeInTz(tank.started_at, timezone)}
              {tank.finished_at && ` – ${formatTimeInTz(tank.finished_at, timezone)}`}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-neutral-400 shrink-0" />
              : <ChevronDown className="h-4 w-4 text-neutral-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {fieldsEditable && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-600 shrink-0">Water in tank</label>
              <input
                type="number"
                min="0"
                step="10"
                inputMode="decimal"
                value={toNum(draft.water_volume_l) ?? ''}
                onChange={e => setDraft(d => ({
                  ...d,
                  water_volume_l: e.target.value === '' ? null : parseFloat(e.target.value),
                }))}
                className="w-28 border border-neutral-300 rounded px-2 py-1.5 text-sm text-right"
              />
              <span className="text-xs text-neutral-500">L</span>
              <label className="flex items-center gap-1.5 text-xs text-neutral-600 ml-auto">
                <input
                  type="checkbox"
                  checked={draft.is_partial}
                  onChange={e => setDraft(d => ({ ...d, is_partial: e.target.checked }))}
                />
                Part fill
              </label>
            </div>
          )}

          {/* Whether the turns count as pumping time for the flow check. The
              next tank carries this forward, so it is set once per spray. */}
          {fieldsEditable ? (
            <label className="flex items-start gap-1.5 text-xs text-neutral-600">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={draft.sprayer_off_on_turns === true}
                onChange={e => setDraft(d => ({ ...d, sprayer_off_on_turns: e.target.checked }))}
              />
              <span>
                Sprayer off during turns
                <span className="block text-neutral-400">
                  Leave unticked if it keeps spraying at the row ends. Carries on to the next tank.
                </span>
              </span>
            </label>
          ) : tank.sprayer_off_on_turns ? (
            <p className="text-xs text-neutral-500">Sprayer off during turns</p>
          ) : null}

          <div className="rounded border border-neutral-200 bg-white px-3 py-1">
            {draft.chemicals.length === 0 ? (
              <p className="text-xs text-neutral-400 py-2">No products recorded on this load.</p>
            ) : (
              draft.chemicals.map(c => (
                <TankChemicalRow
                  key={c.id}
                  chem={c}
                  editable={fieldsEditable}
                  onChange={patch => patchChemical(c.chemical_id, patch)}
                />
              ))
            )}
          </div>

          {tank.area_sprayed_ha != null && (
            <p className={`text-xs flex items-center gap-1 ${AREA_SOURCE_TONE[tank.area_source]}`}>
              <Satellite className="h-3 w-3 shrink-0" />
              {fmt(tank.area_sprayed_ha, 2)} ha covered — {AREA_SOURCE_LABEL[tank.area_source]}
            </p>
          )}

          <TankRateReadout tank={tank} />

          {motion && <TankMotionReadout tank={tank} motion={motion} />}

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex items-center gap-2 pt-1">
            {fieldsEditable && (
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1.5 bg-neutral-800 hover:bg-neutral-900 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Save tank
              </button>
            )}
            {!fieldsEditable && canAmend && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 text-xs text-neutral-600 hover:text-neutral-900 px-2 py-1.5"
              >
                <Pencil className="h-3 w-3" />
                Correct
              </button>
            )}
            {canAmend && (
              <button
                type="button"
                onClick={remove}
                disabled={saving}
                className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 px-2 py-1.5 ml-auto disabled:opacity-50"
              >
                <Trash2 className="h-3 w-3" />
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Panel
// ============================================================================

/**
 * The tank log: what was actually mixed, load by load.
 *
 * "Start tank 1" and "Next tank" are the same action — starting a load closes
 * the previous one, because in the field the only signal that a tank is
 * finished is the next one being mixed. Each new load arrives pre-filled with
 * the water volume and per-product amounts the plan expects, so the operator
 * confirms rather than composes; four numbers typed from scratch in gloves
 * quietly stops happening after a fortnight.
 */
function TankLogPanel({ spray, canRecord, canAmend, onChange }: TankLogPanelProps) {
  const [tanks, setTanks]     = useState<SprayTankLoad[]>([]);
  const [motion, setMotion]   = useState<Map<number, TankMotion>>(new Map());
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await sprayService.getTankLoads(spray.id);
    if (res.success && res.data) setTanks(res.data);
    setLoading(false);

    // After the tanks, never instead of them: the log must render even when
    // the track cannot be read, and a failure here just leaves it unexplained.
    try {
      const m = await sprayService.getTankMotion(spray.id);
      if (m.success && m.data) {
        setMotion(new Map(m.data.tanks.map(t => [t.tank_load_id, t])));
      }
    } catch { /* diagnostic only */ }
  }, [spray.id]);

  useEffect(() => { load(); }, [load]);

  const openTank = tanks.find(t => !t.finished_at);
  const isActive = spray.status === 'in_progress';

  const startNext = async () => {
    setStarting(true);
    setError(null);

    // A tank's start position is worth having even when full GPS tracking is
    // off — it puts the load on the map. Failing to get a fix must not stop
    // the record being made.
    let lat: number | null = null;
    let lng: number | null = null;
    try {
      const loc = await requestLocationWithDisclosure();
      if (loc) {
        lat = loc.coords.latitude;
        lng = loc.coords.longitude;
      }
    } catch { /* no fix — record the tank anyway */ }

    const payload: StartTankInput = { start_lat: lat, start_lng: lng };
    const res = await sprayService.startTankLoad(spray.id, payload);
    setStarting(false);
    if (res.success) {
      // Spraying starts when a tank does, so the track should too. Left to a
      // separate button it gets forgotten, and a tank with no track has no
      // GPS-derived area — it falls back to a typed or apportioned figure.
      // A no-op if this spray is already tracking; a GPS failure surfaces on
      // the tracker card rather than blocking the tank record.
      void trackingController.start({ type: 'spray', id: spray.id }).catch(() => {});
      await load();
      onChange?.();
    } else {
      setError((res as { error?: { message?: string } }).error?.message || 'Could not start the tank');
    }
  };

  // Each load's check is stored when it closes, so a spray judged before the
  // arithmetic improved keeps its old verdicts. Re-running the reconciliation
  // recomputes and stores them all. A whole-spray area somebody typed is
  // passed back so re-checking never swaps it for the GPS figure.
  const [rechecking, setRechecking] = useState(false);
  const recheck = async () => {
    setRechecking(true);
    setError(null);
    const typedArea = spray.actual_area_source === 'manual' ? toNum(spray.actual_area_ha) : null;
    const res = await sprayService.reconcile(spray.id, typedArea);
    setRechecking(false);
    if (res.success) {
      await load();
      onChange?.();
    } else {
      setError((res as { error?: { message?: string } }).error?.message || 'Could not re-check the tanks');
    }
  };

  const finish = async () => {
    setStarting(true);
    const res = await sprayService.finishTankLoad(spray.id);
    setStarting(false);
    if (res.success) {
      await load();
      onChange?.();
    }
  };

  // While spraying, newest first — the open tank sits under the buttons rather
  // than at the bottom of a growing list. A finished spray reads as a record,
  // in the order it happened.
  const orderedTanks = isActive
    ? [...tanks].sort((a, b) => b.tank_number - a.tank_number)
    : tanks;

  // The whole point of checking per tank rather than at sign-off: say it while
  // there are still loads to put right. Only the most recently JUDGED load
  // counts — an `ok` one after a bad one is the operator having fixed it, and
  // an unverifiable one in between proves nothing either way.
  const lastJudged = isActive ? latestJudgedLoad(tanks) : null;
  const concern = lastJudged ? readTankRate(lastJudged) : null;
  const showConcern = concern && (concern.tone === 'watch' || concern.tone === 'alert');

  const totalWater = tanks.reduce((sum, t) => sum + (toNum(t.water_volume_l) ?? 0), 0);

  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <p className="text-sm text-neutral-400">Loading tank log…</p>
      </div>
    );
  }

  // Nothing to show and nothing to do: a completed spray with no tank log is a
  // spray from before tank tracking, and an empty panel would just be noise.
  if (!tanks.length && !canRecord) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Beaker className="h-5 w-5 text-primary-600" />
          <h3 className="font-medium text-sm text-neutral-800">Tank log</h3>
        </div>
        {tanks.length > 0 && (
          <p className="text-xs text-neutral-500">
            {tanks.length} {tanks.length === 1 ? 'tank' : 'tanks'} · {totalWater.toFixed(0)} L water
          </p>
        )}
      </div>

      {/* The actions lead: mid-spray, starting the next load is the one thing
          the operator is here to do, and after a dozen tanks a button below the
          list is a scroll away every time. */}
      {canRecord && isActive && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={startNext}
            disabled={starting}
            className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-base font-semibold px-4 py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            {tanks.length === 0 ? 'Start tank 1' : `Start tank ${tanks.length + 1}`}
          </button>

          {openTank && (
            <button
              type="button"
              onClick={finish}
              disabled={starting}
              className="flex items-center justify-center gap-1.5 border border-neutral-300 hover:bg-neutral-50 text-neutral-700 text-base font-medium px-4 py-3 rounded-lg transition-colors disabled:opacity-50"
            >
              <Check className="h-5 w-5" />
              Tank {openTank.tank_number} empty
            </button>
          )}
        </div>
      )}

      {canRecord && isActive && openTank && (
        <p className="text-xs text-neutral-500 flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0" />
          Tank {openTank.tank_number} is open. Starting the next one closes it.
        </p>
      )}

      {showConcern && lastJudged && concern && (
        <div className={`rounded-lg border px-3 py-2 ${
          concern.tone === 'alert'
            ? 'border-red-300 bg-red-50'
            : 'border-amber-300 bg-amber-50'
        }`}>
          <p className={`text-sm font-semibold flex items-start gap-1.5 ${RATE_TONE[concern.tone]}`}>
            <Gauge className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Tank {lastJudged.tank_number} went out at {concern.headline}</span>
          </p>
          {concern.detail && (
            <p className="text-xs text-neutral-600 mt-1">{concern.detail}</p>
          )}
          <p className="text-xs text-neutral-500 mt-1">
            Worth a check before the next load. Nothing here stops you spraying.
          </p>
        </div>
      )}

      {canAmend && !isActive && tanks.some(t => t.finished_at) && (
        <button
          type="button"
          onClick={recheck}
          disabled={rechecking}
          className="flex items-center gap-1.5 text-xs text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
        >
          {rechecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Gauge className="h-3 w-3" />}
          Re-check tanks
        </button>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      {tanks.length === 0 && (
        <p className="text-xs text-neutral-500">
          No tanks recorded yet. Start a tank when you mix the first load — each one is
          pre-filled with what the plan expects, so you only change what differed.
        </p>
      )}

      <SpeedByTank motion={[...motion.values()]} />
      <TurnWaterSummary motion={[...motion.values()]} tanks={tanks} />

      <div className="space-y-2">
        {orderedTanks.map(t => (
          <TankCard
            key={t.id}
            tank={t}
            spray={spray}
            motion={motion.get(t.id)}
            editable={canRecord && isActive}
            canAmend={canAmend}
            onSaved={() => { load(); onChange?.(); }}
          />
        ))}
      </div>
    </div>
  );
}

export default TankLogPanel;
